import { useExperiment } from '@m/hooks/data/experiments';
import { getQAContactFieldsOverride } from 'apps/customer/utils/qaContactFieldsOverride';

import type { ContactInfoFields } from '../contactInfoDisplay';

export const CONTACT_INFO_FIELDS_EXPERIMENT_NAME = 'web_onboarding_contact_fields';

/**
 * Onboarding waits on this experiment before rendering, so cap the wait: past this the customer
 * gets control (email only). 5s sits above the customer-app p99 for /api/experiment (~2.6s, Datadog
 * RUM, Oct 2026) and matches the backend's own ~5s evaluation timeout, so it only cuts requests
 * that were going to fail or stall anyway (~0.5%).
 */
const EVALUATION_TIMEOUT_MS = 5000;

export type ContactInfoFieldsExperimentState =
  { fields: ContactInfoFields; isPending: false } | { fields: null; isPending: true };

/**
 * Email-only (control) vs name-and-email (test) on the optional contact step — start visit and
 * agnostic sign-up only (EN-14552). Flows outside the test pass `enabled: false` and keep
 * `fallback`. Only fetched while enabled, so exposure is logged when the step is actually shown.
 * A failed fetch resolves to control (`useExperiment` falls back to an empty-parameter control
 * response), so nobody is stuck on a loading screen. Outside production, ?contactFields= forces an
 * arm for QA without asking Statsig.
 */
export function useContactInfoFieldsExperiment({
  enabled,
  fallback,
}: {
  enabled: boolean;
  fallback: ContactInfoFields;
}): ContactInfoFieldsExperimentState {
  const qaOverride = getQAContactFieldsOverride();
  const { data, isPending } = useExperiment(
    CONTACT_INFO_FIELDS_EXPERIMENT_NAME,
    undefined,
    enabled && qaOverride === null,
    {
      staleTime: Infinity,
      timeoutMs: EVALUATION_TIMEOUT_MS,
    },
  );

  if (!enabled) return { fields: fallback, isPending: false };
  if (qaOverride) return { fields: qaOverride, isPending: false };
  if (isPending) return { fields: null, isPending: true };
  return {
    fields: data?.parameters?.collect_name === true ? 'nameAndEmail' : 'email',
    isPending: false,
  };
}
